import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Account, DealsResponse, HomeResponse, NavNode, ShopCard } from "./types";
import { Go, type GoFn } from "./route";
import { deptRoute } from "./nav";
import { get, rands } from "./shared";
import { Countdown } from "./ui";
import { Pic } from "./img";
import { setHomeSeo } from "./seo";
import { ProductCard } from "./Listing";
import { HeroCarousel } from "./HeroCarousel";
import { ReviewsSection, useReviews } from "./Reviews";
import { HomeAppSection } from "./AppPromo";
import { CampaignBanner } from "./campaign-theme";
import { IconChevronLeft, IconChevronRight, IconReturn, IconShield, IconTruck } from "./icons";

/** The home data already fetched this visit, so coming back to Home costs nothing. */
let loaded: HomeResponse | null = null;

const TRUST = [
  { icon: <IconTruck size={28} />, head: "Delivery 2–7 working days", body: "Nationwide, or collect in store" },
  { icon: <IconShield size={28} />, head: "Secure checkout", body: "Card and instant EFT" },
  { icon: <IconReturn size={28} />, head: "14-day returns", body: "On unused goods" },
];

/** A horizontally scrolling strip with scroll-snap and previous/next buttons. No library. */
export function Strip({ label, children, slideClass }: { label: string; children: ReactNode; slideClass?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const by = (dir: 1 | -1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  return (
    <div className="strip-wrap">
      <button type="button" className="strip-btn prev" aria-label={`${label}: previous`} onClick={() => by(-1)}><IconChevronLeft size={20} /></button>
      <div ref={ref} className={`strip-track ${slideClass ?? ""}`} role="list" aria-label={label} tabIndex={0}>{children}</div>
      <button type="button" className="strip-btn next" aria-label={`${label}: next`} onClick={() => by(1)}><IconChevronRight size={20} /></button>
    </div>
  );
}

export function HomePage({ nav, go, account }: { nav: NavNode[]; go: GoFn; account: Account | null }) {
  const [home, setHome] = useState<HomeResponse | null>(loaded);
  const [slide, setSlide] = useState(0);
  const heroRef = useRef<HTMLDivElement | null>(null);
  const trade = account?.list === "wholesale";
  const reviews = useReviews();

  useEffect(() => { setHomeSeo(); }, []);

  /** ONE call for both strips and every tile image — it used to be six whole department pages. */
  useEffect(() => {
    if (loaded) return;
    const ac = new AbortController();
    get<HomeResponse>("/shop/home", ac.signal)
      .then((d) => { loaded = d; if (!ac.signal.aborted) setHome(d); })
      .catch(() => { /* the strips simply stay hidden; the rest of the page does not depend on them */ });
    return () => ac.abort();
  }, []);

  /**
   * Deal of the day + Clearance. Fetched on every visit (it changes at midnight), and again when
   * the countdown reaches zero — `after=<old endsAt>` only defeats the browser's own HTTP cache;
   * the server keys the answer on its own SAST date and ignores the parameter.
   */
  const [deals, setDeals] = useState<DealsResponse | null>(null);
  const [dealsKey, setDealsKey] = useState("");
  useEffect(() => {
    const ac = new AbortController();
    get<DealsResponse>(`/shop/deals${dealsKey ? `?after=${encodeURIComponent(dealsKey)}` : ""}`, ac.signal)
      .then((d) => { if (!ac.signal.aborted) setDeals(d); })
      .catch(() => { /* no deal block — never a broken home page */ });
    return () => ac.abort();
  }, [dealsKey]);
  const daily = deals?.daily ?? null;

  const top = [...nav].sort((a, b) => b.productCount - a.productCount).slice(0, 6);
  const imageOf = (id: string) => home?.departmentImages[id] ?? null;
  const slides = top.filter((n) => imageOf(n.id)).slice(0, 4);
  const specials: ShopCard[] = home?.specials ?? [];
  const popular: ShopCard[] = home?.popular ?? [];
  const promos = top.filter((n) => imageOf(n.id)).slice(0, 6);

  const onHeroScroll = () => {
    const el = heroRef.current;
    if (el && el.clientWidth > 0) setSlide(Math.round(el.scrollLeft / el.clientWidth));
  };
  const showSlide = (i: number) => {
    const el = heroRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  return (
    <>
      <HeroCarousel nav={nav} go={go} />

      <section className="trust-strip" aria-label="Why shop with us">
        <ul className="wrap">{TRUST.map((t) => <li key={t.head}>{t.icon}<span><b>{t.head}</b>{t.body}</span></li>)}</ul>
      </section>

      {/** Holiday campaigns (teaser/live), above Deal of the day — shown to trade too: same pages, own prices. */}
      <CampaignBanner go={go} />

      {daily && !trade && (
        <section className="wrap home-sec" id="deal" aria-labelledby="h-deal">
          <div className="deal-day">
            <Go to={{ name: "product", handle: daily.card.handle }} go={go} className="deal-img">
              {daily.card.image ? <Pic src={daily.card.image} alt={daily.card.imageAlt ?? daily.card.title} sizes="(min-width: 900px) 40vw, 100vw" width={640} height={640} eager /> : <div className="skel fill" />}
            </Go>
            <div className="deal-body">
              <h2 id="h-deal" className="deal-kicker">Deal of the day</h2>
              <p className="deal-title"><Go to={{ name: "product", handle: daily.card.handle }} go={go}>{daily.card.title}</Go></p>
              <p className="deal-price money">
                <b className="sale">{rands(daily.card.priceCents)}</b>
                {daily.card.wasCents !== null && <s>{rands(daily.card.wasCents)}</s>}
                {daily.card.wasCents && daily.card.priceCents !== null && (
                  <span className="save-flag inline">{Math.round(((daily.card.wasCents - daily.card.priceCents) / daily.card.wasCents) * 100)}% off</span>
                )}
              </p>
              <p className="deal-ends">Ends in <Countdown endsAt={daily.endsAt} onEnd={() => setDealsKey(daily.endsAt)} /></p>
              {daily.maxPerOrder && <p className="deal-limit">Limit {daily.maxPerOrder} per order · while stock lasts</p>}
              <Go to={{ name: "product", handle: daily.card.handle }} go={go} className="btn btn-action">See the deal</Go>
            </div>
          </div>
        </section>
      )}

      <section className="wrap home-sec" aria-labelledby="h-depts">
        <h2 id="h-depts">Shop by department</h2>
        <ul className="round-tiles">
          {nav.map((n) => {
            const img = imageOf(n.id);
            return (
              <li key={n.id}>
                <Go to={deptRoute(n)} go={go} className="round-tile">
                  <span className="round-img">{img ? <Pic src={img} alt={n.name} sizes="120px" width={120} height={120} /> : <b aria-hidden="true">{n.name.slice(0, 1)}</b>}</span>
                  <span className="round-name">{n.name}</span>
                </Go>
              </li>
            );
          })}
        </ul>
      </section>

      {specials.length > 0 && (
        <section className="wrap home-sec" id="specials" aria-labelledby="h-spec">
          <h2 id="h-spec">Specials</h2>
          <Strip label="Specials">{specials.map((p, i) => <div role="listitem" key={p.handle}><ProductCard p={p} i={i + 4} go={go} trade={trade} /></div>)}</Strip>
        </section>
      )}

      {!trade && deals && deals.clearance.length > 0 && (
        <section className="wrap home-sec" aria-labelledby="h-clr">
          <div className="sec-head"><h2 id="h-clr">Clearance</h2><Go to={{ name: "clearance" }} go={go} className="sec-link">View all {deals.clearanceCount} →</Go></div>
          <Strip label="Clearance">{deals.clearance.map((p, i) => <div role="listitem" key={p.handle}><ProductCard p={p} i={i + 4} go={go} trade={trade} /></div>)}</Strip>
        </section>
      )}

      {popular.length > 0 && top[0] && (
        <section className="wrap home-sec" aria-labelledby="h-pop">
          <div className="sec-head"><h2 id="h-pop">Popular in {top[0].name}</h2><Go to={deptRoute(top[0])} go={go} className="sec-link">Shop all {top[0].name} →</Go></div>
          <Strip label={`Popular in ${top[0].name}`}>{popular.map((p, i) => <div role="listitem" key={p.handle}><ProductCard p={p} i={i + 4} go={go} trade={trade} /></div>)}</Strip>
        </section>
      )}

      <HomeAppSection />

      {reviews && <ReviewsSection r={reviews} />}

      {promos.length > 0 && (
        <section className="wrap home-sec" aria-labelledby="h-promo">
          <h2 id="h-promo">Browse the range</h2>
          <div className="promos">
            {promos.map((n) => (
              <Go key={n.id} to={deptRoute(n)} go={go} className="promo">
                <div className="promo-img"><Pic src={imageOf(n.id)!} alt={n.name} sizes="(min-width: 1000px) 16vw, (min-width: 640px) 33vw, 50vw" width={400} height={400} /></div>
                <div className="promo-body"><h3>{n.name}</h3><span>{n.productCount} products →</span></div>
              </Go>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
